// src/utils/biqChatHistoryStorage.js
// API client for the BiQ "Recent chats" sidebar and message restore.
//
// Backed by the conversation endpoints on the same container-chat-app host as
// POST /chat (see resolveBiqServiceUrl / getToken in ./api, reused here so auth
// header + host resolution never drift from the /chat request). Auth is the
// Firebase ID token only — the backend derives the user from it, so we never
// send an email/user id in these requests.
//
// Everything here is defensive: a network failure, a missing token, or a
// malformed response must never take the sidebar down, so every function
// degrades to an empty list/null/false rather than throwing out to the caller.
//
// The ONE exception is fetchConversations, which reports an outcome instead
// of a bare array — see LIST_OK/LIST_UNAUTHENTICATED/LIST_FAILED below. A
// bare [] conflated three very different things: "you have no chats", "your
// token wasn't ready yet", and "the request failed".

const TITLE_MAX_LENGTH = 60;

/** The list is authoritative: the user really has exactly these conversations. */
export const LIST_OK = 'ok';
/** No Firebase ID token yet — getToken() returns null until `auth.currentUser` */
export const LIST_UNAUTHENTICATED = 'unauthenticated';
/** Sent, but no usable answer (network error, non-2xx, malformed body). Retryable. */
export const LIST_FAILED = 'failed';

/**
 * Sidebar label for a thread: the first thing the user actually asked.
 * Report-generation turns skip the user bubble, so fall back to `userPrompt`.
 * Still useful as an optimistic local title before the backend's real
 * `conversation_title` SSE event (or the persisted `title`) arrives.
 */
export function deriveChatTitle(messages) {
  const source = (messages || []).find(
    (m) =>
      (m?.type === 'user' && typeof m.content === 'string' && m.content.trim()) ||
      (typeof m?.userPrompt === 'string' && m.userPrompt.trim())
  );
  if (!source) return '';

  const raw = (source.type === 'user' ? source.content : source.userPrompt) || '';
  const title = raw.trim().replace(/\s+/g, ' ');
  if (!title) return '';
  return title.length > TITLE_MAX_LENGTH
    ? `${title.slice(0, TITLE_MAX_LENGTH).trimEnd()}…`
    : title;
}

async function authedFetch(path, init) {
  // Dynamic import: ./api pulls in firebase + axios + Vite env at module load.
  // Deferring it here keeps deriveChatTitle (the pure function
  // above) importable with zero side effects — see biqChatHistoryStorage.check.mjs.
  const { getToken } = await import('./api');
  const { BIQ_CONVERSATIONS_BASE } = await import('../constants/URLs');
  const token = await getToken();
  if (!token) return null; // not signed in — nothing to fetch/mutate yet
  // Separate service from POST /chat (conversation_sessions Cloud Function,
  // not container_chat_app) — see BIQ_CONVERSATIONS_BASE's own comment for
  // why this isn't resolveBiqServiceUrl.
  return fetch(`${BIQ_CONVERSATIONS_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(init?.headers || {}),
    },
  });
}

/**
 * GET /conversations?limit=30
 * @param {number} [limit]
 * @returns {Promise<{status: string, conversations: Array<{conversation_id:string, title:string|null, title_source:string|null, created_at:string, updated_at:string, message_count:number, last_message_preview:string}>}>}
 */
export async function fetchConversations(limit = 30) {
  try {
    const res = await authedFetch(`/conversations?limit=${encodeURIComponent(limit)}`, {
      method: 'GET',
    });
    // Don't read a body we already know we won't trust.
    if (!res || !res.ok) return toConversationsResult(res, null);
    return toConversationsResult(res, await res.json());
  } catch {
    return { status: LIST_FAILED, conversations: [] };
  }
}

/**
 * Pure outcome mapping for fetchConversations, split out so it's assertable
 * without the ./api import chain (see biqChatHistoryStorage.check.mjs).
 * @param {{ok: boolean}|null} res Response, or null when authedFetch had no token.
 * @param {unknown} data Parsed body, or null when it was never read.
 */
export function toConversationsResult(res, data) {
  // authedFetch returns null for exactly one reason: no ID token yet.
  if (!res) return { status: LIST_UNAUTHENTICATED, conversations: [] };
  if (!res.ok) return { status: LIST_FAILED, conversations: [] };
  // A 200 whose body isn't the documented shape is a failure, not an empty
  // account — reporting it as OK would paint "No chats yet" over real data.
  if (!Array.isArray(data?.conversations)) {
    return { status: LIST_FAILED, conversations: [] };
  }
  return { status: LIST_OK, conversations: data.conversations };
}

/**
 * GET /conversations/<id>/messages?limit=50
 * @param {string} conversationId
 * @param {number} [limit]
 * @returns {Promise<Array<{message_id:string, chat_id:string|null, role:'user'|'assistant', content:string, timestamp:string}>>}
 *   Chronological (oldest first). Empty array on 404 / any failure.
 */
export async function fetchConversationMessages(conversationId, limit = 50) {
  if (!conversationId) return [];
  try {
    const res = await authedFetch(
      `/conversations/${encodeURIComponent(conversationId)}/messages?limit=${encodeURIComponent(limit)}`,
      { method: 'GET' }
    );
    if (!res || !res.ok) return [];
    const data = await res.json();
    return Array.isArray(data?.messages) ? data.messages : [];
  } catch {
    return [];
  }
}

/**
 * PATCH /conversations/<id> { title } — sets title_source to "user", which
 * always wins over a later automatic title write.
 * @param {string} conversationId
 * @param {string} title
 * @returns {Promise<{conversation_id:string, title:string, title_source:'user'}|null>} null on failure.
 */
export async function renameConversation(conversationId, title) {
  if (!conversationId) return null;
  try {
    const res = await authedFetch(`/conversations/${encodeURIComponent(conversationId)}`, {
      method: 'PATCH',
      body: JSON.stringify({ title }),
    });
    if (!res || !res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

/**
 * DELETE /conversations/<id> — deletes the conversation's messages and session doc.
 * @param {string} conversationId
 * @returns {Promise<boolean>} true only on a confirmed { deleted: true }.
 */
export async function deleteConversation(conversationId) {
  if (!conversationId) return false;
  try {
    const res = await authedFetch(`/conversations/${encodeURIComponent(conversationId)}`, {
      method: 'DELETE',
    });
    if (!res || !res.ok) return false;
    const data = await res.json();
    return data?.deleted === true;
  } catch {
    return false;
  }
}
